/**
 * Ring size localization — display layer only.
 *
 * Shopify always stores US ring sizes (4-11). Liquid renders the label for the
 * customer's market; this element handles the manual override chip and keeps
 * the labels correct after the theme's variant picker rewrites them.
 *
 * Two kinds of hooks are rendered by Liquid:
 *   [data-ring-size="4"] - a fixed size (the pills, the sticky bar title).
 *   [data-ring-size-live] - the "Ring Size: 4" legend and the collapsed
 *       dropdown button. The theme resets these to the raw US value on every
 *       variant change, so the canonical value is read back from the checked
 *       input of the surrounding fieldset instead of from an attribute.
 *
 * Variant ids and option values are never touched, so the cart always gets the
 * US variant the customer clicked.
 */
(function () {
  'use strict';

  if (customElements.get('hdt-ring-size')) return;

  var STORAGE_KEY = 'ringSizeSystem';
  var SYSTEMS = ['US', 'CA', 'UK', 'EU', 'AU', 'NZ'];
  // Systems whose labels are the stored option value verbatim — see syncCartProperty.
  var NUMERIC_US_SYSTEMS = ['US', 'CA'];
  var STICKY_SELECTOR = '.hdt-sticky-add-to-cart';
  var maps = null;
  var flags = {};

  function readStored() {
    try {
      var value = window.localStorage.getItem(STORAGE_KEY);
      return SYSTEMS.indexOf(value) > -1 ? value : null;
    } catch (e) {
      return null;
    }
  }

  function writeStored(system) {
    try {
      window.localStorage.setItem(STORAGE_KEY, system);
    } catch (e) {
      /* private mode / storage disabled — the market default still applies */
    }
  }

  /**
   * Option values are authored with a suffix ("4 US"), so reduce them to the
   * bare US number the size tables are keyed by.
   */
  function normalize(value) {
    return (value == null ? '' : String(value))
      .trim()
      .split(/\s+/)[0]
      .replace(/us/gi, '')
      .trim();
  }

  function labelFor(usValue, system) {
    var raw = usValue == null ? '' : String(usValue).trim();
    if (!maps || !maps[system]) return raw;
    return maps[system][normalize(raw)] || raw;
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  /** The canonical US value currently selected in this option's fieldset. */
  function selectedUsValue(node) {
    var fieldset = node.closest('fieldset');
    if (!fieldset) return null;
    var input =
      fieldset.querySelector('input[is-value]:checked') ||
      fieldset.querySelector('[is-value][aria-selected="true"]') ||
      fieldset.querySelector('input[is-value][type="hidden"], [trigger-value]');
    return input ? input.value : null;
  }

  /**
   * Mirrors the shown equivalent ("47 EU") into each product form's hidden
   * `properties[ring_size_equivalent]` input so the cart line records what the
   * shopper actually saw. Disabled entirely when the shown system is US or CA —
   * both label sizes with the stored US number, so the property would only
   * repeat the option value.
   */
  function syncCartProperty(system) {
    document.querySelectorAll('hdt-variant-picker[form]').forEach(function (picker) {
      var form = document.getElementById(picker.getAttribute('form') || '');
      var input = form ? form.querySelector('input[data-ring-size-property]') : null;
      if (!input) return;
      var fieldset = picker.querySelector('fieldset[is-ring-size]');
      var usValue = fieldset ? selectedUsValue(fieldset) : null;
      var mapped = usValue != null && maps && maps[system] ? maps[system][normalize(usValue)] : null;
      if (NUMERIC_US_SYSTEMS.indexOf(system) > -1 || !mapped) {
        input.value = '';
        input.disabled = true;
      } else {
        input.value = mapped + ' ' + system;
        input.disabled = false;
      }
    });
  }

  /** Relabels every ring size node inside `root`. */
  function relabel(root, system) {
    if (!root) return;
    root.querySelectorAll('[data-ring-size]').forEach(function (node) {
      setText(node, labelFor(node.getAttribute('data-ring-size'), system));
    });
    root.querySelectorAll('[data-ring-size-live]').forEach(function (node) {
      var value = selectedUsValue(node);
      if (value == null) return;
      setText(node, labelFor(value, system));
    });
  }

  var RingSize = class extends HTMLElement {
    connectedCallback() {
      if (this.rsReady) return;
      this.rsReady = true;

      this.marketSystem = this.getAttribute('data-ring-size-system-active') || 'US';
      this.chipFlag = this.querySelector('[data-ring-size-flag]');
      this.chipCode = this.querySelector('[data-ring-size-code]');
      this.scope = this.closest('hdt-variant-picker') || this.closest('.hdt-product-info__item') || document.body;

      if (!maps) {
        var json = this.querySelector('script[data-ring-size-map]');
        try {
          maps = JSON.parse(json ? json.textContent : '{}');
        } catch (e) {
          maps = {};
        }
      }

      // Remember each system's flag markup so the chip can swap without a fetch.
      this.querySelectorAll('[data-ring-size-system]').forEach(function (option) {
        var system = option.getAttribute('data-ring-size-system');
        var flag = option.querySelector('.hdt-ring-size__flag');
        if (system && flag && !flags[system]) flags[system] = flag.innerHTML;
      });

      this.addEventListener('click', this.onOptionClick);

      // The theme resets [update-value] to the raw US value on every variant
      // change, and the sticky bar swaps its variant title wholesale — repaint
      // whenever either happens.
      this.observer = new MutationObserver(this.onMutate);
      this.observer.observe(this.scope, { childList: true, subtree: true, characterData: true });

      var sticky = document.querySelector(STICKY_SELECTOR);
      if (sticky) {
        this.stickyObserver = new MutationObserver(this.onMutate);
        this.stickyObserver.observe(sticky, { childList: true, subtree: true, characterData: true });
      }

      this.system = readStored() || this.marketSystem;
      // Liquid already rendered the market system server side, so only repaint
      // when the customer has overridden it (no flash on first load).
      if (this.system !== this.marketSystem) this.paint(this.system);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.stickyObserver) this.stickyObserver.disconnect();
      this.rsReady = false;
    }

    onMutate = () => {
      if (this.rsPending) return;
      this.rsPending = true;
      // Batch: the theme can touch several nodes in the same tick.
      Promise.resolve().then(() => {
        this.rsPending = false;
        this.paint(this.system);
      });
    };

    onOptionClick = (event) => {
      var trigger = event.target.closest && event.target.closest('[data-ring-size-system]');
      if (!trigger || !this.contains(trigger)) return;
      var system = trigger.getAttribute('data-ring-size-system');
      if (SYSTEMS.indexOf(system) === -1) return;
      writeStored(system);
      // Keep every picker on the page (product page + quick view) in sync.
      document.querySelectorAll('hdt-ring-size').forEach(function (el) {
        el.paint(system);
      });
      var popover = trigger.closest('hdt-popover');
      if (popover && typeof popover.close === 'function') popover.close();
    };

    /** Paints labels and the chip for `system`. */
    paint(system) {
      if (!this.scope || SYSTEMS.indexOf(system) === -1) return;
      this.system = system;

      relabel(this.scope, system);
      relabel(document.querySelector(STICKY_SELECTOR), system);
      syncCartProperty(system);

      setText(this.chipCode, system);
      if (this.chipFlag && flags[system] && this.chipFlag.innerHTML !== flags[system]) {
        this.chipFlag.innerHTML = flags[system];
      }
      this.querySelectorAll('[data-ring-size-system]').forEach(function (option) {
        option.setAttribute(
          'aria-selected',
          option.getAttribute('data-ring-size-system') === system ? 'true' : 'false'
        );
      });
    }
  };

  customElements.define('hdt-ring-size', RingSize);
})();
